import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { DISCLOSURES, SMS_CONSENT_VERSION } from "@/config/site";
import { cn } from "@/lib/utils";

const API = process.env.REACT_APP_BACKEND_URL;
const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function readUTM() {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get("utm_source") || "",
    utm_medium: p.get("utm_medium") || "",
    utm_campaign: p.get("utm_campaign") || "",
  };
}

/**
 * Reusable, accessible inquiry form.
 * - Validates locally, then POSTs to a secure server endpoint that emails
 *   Legacy Spa. Nothing is stored client-side or in a database.
 * - Focuses the first invalid field on submit; announces success/failure.
 * - Includes hidden UTM fields + a honeypot spam field (server-checked).
 */
export const ProtoForm = ({
  formId,
  endpoint,
  fields,
  consentLabel = "I agree to be contacted about my inquiry.",
  submitLabel = "Submit",
  testId = "proto-form",
  tone = "dark",
  smsOptIn = false,
}) => {
  const isLight = tone === "light";
  const initial = useMemo(() => {
    const o = {};
    fields.forEach((f) => (o[f.name] = f.type === "checkbox" ? false : ""));
    o.consent = false;
    o.company_website = ""; // honeypot
    o.sms_consent = false; // marketing SMS opt-in (never pre-checked)
    o.sms_consent_version = SMS_CONSENT_VERSION;
    return o;
  }, [fields]);

  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [sendError, setSendError] = useState(false);
  const [errorMsg, setErrorMsg] = useState(DISCLOSURES.formError);
  const refs = useRef({});
  const utm = useRef(readUTM());
  // Field element ids are scoped to the unique testId so they can never
  // collide with page-level elements (e.g. the mailto link on /contact).
  const fid = (n) => `${testId}-${n}`;

  const setField = (name, value) => {
    setValues((v) => ({ ...v, [name]: value }));
    setErrors((e) => (e[name] ? { ...e, [name]: undefined } : e));
  };

  const validate = () => {
    const next = {};
    fields.forEach((f) => {
      const val = values[f.name];
      if (f.required && (val === "" || val === false || val == null)) {
        next[f.name] = `${f.label} is required.`;
      } else if (f.type === "email" && val && !emailRe.test(val)) {
        next[f.name] = "Please enter a valid email address.";
      }
    });
    if (!values.consent) next.consent = "Please provide consent to continue.";
    return next;
  };

  const focusFirst = (errObj) => {
    const order = [...fields.map((f) => f.name), "consent"];
    const first = order.find((n) => errObj[n]);
    if (first && refs.current[first]) refs.current[first].focus();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return; // prevent duplicate submissions
    const next = validate();
    if (Object.keys(next).length) {
      setErrors(next);
      focusFirst(next);
      toast.error("Please review the highlighted fields.");
      return;
    }
    setSendError(false);
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/forms/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...values, ...utm.current }),
      });
      if (!res.ok) {
        // Distinct copy for rate-limited requests.
        setErrorMsg(res.status === 429 ? DISCLOSURES.formRateLimit : DISCLOSURES.formError);
        throw new Error(`status ${res.status}`);
      }
      setDone(true);
      toast.success("Message sent", { description: DISCLOSURES.formSuccess });
    } catch (err) {
      setSendError(true);
      toast.error("Couldn’t send your request");
    } finally {
      setSubmitting(false);
    }
  };

  const labelCls = cn(
    "block text-sm font-medium mb-2",
    isLight ? "text-legacy-charcoal/80" : "text-white/80"
  );
  const inputCls = cn(
    "w-full rounded-xl border bg-transparent px-4 py-3 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-legacy-mint",
    isLight
      ? "border-legacy-charcoal/20 text-legacy-charcoal placeholder:text-legacy-charcoal/40"
      : "border-white/15 text-white placeholder:text-white/35"
  );
  const errCls = "mt-1.5 text-xs text-red-400";

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className={cn(
          "rounded-2xl border p-8 text-center",
          isLight
            ? "border-legacy-mint/40 bg-legacy-softmint"
            : "border-legacy-mint/30 bg-legacy-mint/[0.06]"
        )}
        role="status"
        data-testid={`${testId}-confirmation`}
      >
        <CheckCircle2 className="mx-auto h-10 w-10 text-legacy-mint" aria-hidden="true" />
        <h3 className={cn("mt-4 font-heading text-2xl", isLight ? "text-legacy-charcoal" : "text-white")}>
          Thank you
        </h3>
        <p className={cn("mt-2 text-sm leading-relaxed", isLight ? "text-legacy-charcoal/70" : "text-white/70")}>
          {DISCLOSURES.formSuccess}
        </p>
      </motion.div>
    );
  }

  return (
    <form
      id={formId}
      onSubmit={onSubmit}
      noValidate
      aria-label={`${(formId || "inquiry").replace(/-/g, " ")} form`}
      className="space-y-6"
      data-testid={testId}
    >
      <AnimatePresence>
        {sendError && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={cn(
              "flex items-start gap-3 rounded-xl border p-4 text-sm leading-relaxed",
              "border-red-400/40 bg-red-500/10",
              isLight ? "text-red-700" : "text-red-200"
            )}
            role="alert"
            data-testid={`${testId}-send-error`}
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" aria-hidden="true" />
            <p>{errorMsg}</p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid gap-5 sm:grid-cols-2">
        {fields.map((f) => {
          const spanFull = f.full || f.type === "textarea";
          const invalid = Boolean(errors[f.name]);
          const describedBy = invalid ? `${fid(f.name)}-error` : undefined;
          return (
            <div key={f.name} className={cn(spanFull && "sm:col-span-2")}>
              <label htmlFor={fid(f.name)} className={labelCls}>
                {f.label}
                {f.required && (
                  <span className="text-legacy-gold" aria-hidden="true"> *</span>
                )}
                {f.required && <span className="sr-only"> (required)</span>}
              </label>

              {f.type === "textarea" ? (
                <textarea
                  id={fid(f.name)}
                  name={f.name}
                  ref={(el) => (refs.current[f.name] = el)}
                  rows={4}
                  required={f.required}
                  aria-required={f.required || undefined}
                  aria-invalid={invalid || undefined}
                  aria-describedby={describedBy}
                  placeholder={f.placeholder}
                  value={values[f.name] ?? ""}
                  onChange={(e) => setField(f.name, e.target.value)}
                  className={cn(inputCls, "resize-y")}
                  data-testid={`${testId}-${f.name}`}
                />
              ) : f.type === "select" ? (
                <select
                  id={fid(f.name)}
                  name={f.name}
                  ref={(el) => (refs.current[f.name] = el)}
                  required={f.required}
                  aria-required={f.required || undefined}
                  aria-invalid={invalid || undefined}
                  aria-describedby={describedBy}
                  value={values[f.name] ?? ""}
                  onChange={(e) => setField(f.name, e.target.value)}
                  className={cn(inputCls, isLight ? "text-legacy-charcoal" : "text-white [&>option]:text-legacy-charcoal")}
                  data-testid={`${testId}-${f.name}`}
                >
                  <option value="">Select…</option>
                  {f.options.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id={fid(f.name)}
                  name={f.name}
                  type={f.type || "text"}
                  ref={(el) => (refs.current[f.name] = el)}
                  required={f.required}
                  aria-required={f.required || undefined}
                  aria-invalid={invalid || undefined}
                  aria-describedby={describedBy}
                  placeholder={f.placeholder}
                  value={values[f.name] ?? ""}
                  onChange={(e) => setField(f.name, e.target.value)}
                  className={inputCls}
                  data-testid={`${testId}-${f.name}`}
                />
              )}

              <AnimatePresence>
                {invalid && (
                  <motion.p
                    id={`${fid(f.name)}-error`}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className={errCls}
                    role="alert"
                    data-testid={`${testId}-${f.name}-error`}
                  >
                    {errors[f.name]}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {/* Honeypot — visually hidden, not announced. */}
      <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
        <label htmlFor={fid("company_website")}>Leave this field empty</label>
        <input
          id={fid("company_website")}
          name="company_website"
          tabIndex={-1}
          autoComplete="off"
          value={values.company_website}
          onChange={(e) => setField("company_website", e.target.value)}
        />
      </div>

      {/* Hidden UTM fields, ready for future use. */}
      <input type="hidden" name="utm_source" value={utm.current.utm_source} />
      <input type="hidden" name="utm_medium" value={utm.current.utm_medium} />
      <input type="hidden" name="utm_campaign" value={utm.current.utm_campaign} />

      {smsOptIn && (
        <div className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <input
            id={fid("sms_consent")}
            name="sms_consent"
            type="checkbox"
            checked={values.sms_consent}
            onChange={(e) => setField("sms_consent", e.target.checked)}
            className="mt-1 h-4 w-4 shrink-0 accent-legacy-mint"
            data-testid={`${testId}-sms-consent`}
          />
          <label
            htmlFor={fid("sms_consent")}
            className={cn("text-sm leading-relaxed", isLight ? "text-legacy-charcoal/80" : "text-white/75")}
          >
            I agree to receive marketing and promotional text messages from
            Legacy Spa at the phone number provided. Message frequency may vary.
            Message and data rates may apply. Consent is not a condition of
            purchase. Reply STOP to opt out and HELP for help. View our{" "}
            <Link
              to="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-legacy-mint underline underline-offset-2 hover:opacity-80"
              data-testid={`${testId}-sms-privacy-link`}
            >
              Privacy Policy
            </Link>{" "}
            and{" "}
            <Link
              to="/terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
              className="text-legacy-mint underline underline-offset-2 hover:opacity-80"
              data-testid={`${testId}-sms-terms-link`}
            >
              Terms &amp; Conditions
            </Link>
            . This is optional and separate from service and appointment messages.
          </label>
        </div>
      )}

      <div className="flex items-start gap-3">
        <input
          id={fid("consent")}
          name="consent"
          type="checkbox"
          ref={(el) => (refs.current.consent = el)}
          checked={values.consent}
          onChange={(e) => setField("consent", e.target.checked)}
          required
          aria-required="true"
          aria-invalid={Boolean(errors.consent) || undefined}
          aria-describedby={errors.consent ? fid("consent-error") : undefined}
          className="mt-1 h-4 w-4 shrink-0 accent-legacy-mint"
          data-testid={`${testId}-consent`}
        />
        <label
          htmlFor={fid("consent")}
          className={cn("text-sm leading-relaxed", isLight ? "text-legacy-charcoal/75" : "text-white/70")}
        >
          {consentLabel}
          <span className="text-legacy-gold" aria-hidden="true"> *</span>
        </label>
      </div>
      {errors.consent && (
        <p id={fid("consent-error")} role="alert" className={errCls} data-testid={`${testId}-consent-error`}>
          {errors.consent}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className={cn(
          "inline-flex items-center justify-center gap-2 rounded-full px-8 py-3.5 text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-legacy-mint focus-visible:ring-offset-2",
          isLight
            ? "bg-legacy-charcoal text-white hover:bg-legacy-charcoal/90 focus-visible:ring-offset-white"
            : "bg-legacy-mint text-legacy-charcoal hover:bg-legacy-mint/90 focus-visible:ring-offset-legacy-charcoal",
          submitting && "opacity-70"
        )}
        data-testid={`${testId}-submit`}
      >
        {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        {submitting ? "Sending…" : submitLabel}
      </button>
    </form>
  );
};
